import { Modal } from './ui/Modal';
import type { KeyboardShortcut } from '../lib/keyboard';

interface KeyboardHelpProps {
  isOpen: boolean;
  onClose: () => void;
  shortcuts: KeyboardShortcut[];
}

/**
 * Help dialog listing every registered keyboard shortcut.  Grouped by
 * `group`; each shortcut renders its combo as monospace key chips.
 */
export function KeyboardHelp({ isOpen, onClose, shortcuts }: KeyboardHelpProps) {
  // Group shortcuts by their `group` field, preserving the order they were
  // registered in.
  const groups: Record<string, KeyboardShortcut[]> = {};
  const groupOrder: string[] = [];
  for (const sc of shortcuts) {
    if (!groups[sc.group]) {
      groups[sc.group] = [];
      groupOrder.push(sc.group);
    }
    groups[sc.group].push(sc);
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Keyboard Shortcuts" size="md">
      <p className="font-sans text-[13.5px] text-[var(--color-ink-muted)]">
        Press <KeyChip k="g" /> then a second key to navigate. Shortcuts are
        ignored while you're typing.
      </p>

      <div className="mt-6 space-y-7">
        {groupOrder.map((groupName) => (
          <section key={groupName}>
            <p className="eyebrow mb-3">{groupName}</p>
            <ul className="space-y-2.5">
              {groups[groupName].map((sc) => (
                <li
                  key={sc.combo}
                  className="flex items-center justify-between gap-4"
                >
                  <span className="font-sans text-[14px] text-[var(--color-ink)]">
                    {sc.label}
                  </span>
                  <span className="flex items-center gap-1.5">
                    {sc.combo.split(' ').map((k, i) => (
                      <KeyChip key={`${k}-${i}`} k={k} />
                    ))}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </Modal>
  );
}

function KeyChip({ k }: { k: string }) {
  return (
    <kbd className="inline-flex h-6 min-w-[24px] items-center justify-center rounded-[3px] border border-[var(--color-rule-strong)] bg-[var(--color-surface-muted)] px-1.5 font-mono text-[11.5px] font-semibold text-[var(--color-ink)]">
      {k}
    </kbd>
  );
}
